import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSitemapData } from "@/lib/sitemap.functions";
import { listPublishedCategories, listPublishedProducts } from "@/lib/catalog.functions";
import { visibleConcepts } from "@/lib/concepts";

const SEGMENT_META: Record<string, { label: string; path: string; desc: string }> = {
  foretag: {
    label: "Företagsevent",
    path: "/foretag",
    desc: "Kickoff, sommarfest, julbord och teambuilding",
  },
  massa: {
    label: "Mässa & event",
    path: "/massa",
    desc: "Aktiveringar i monter och publika event",
  },
  skola: {
    label: "Skola (F–9)",
    path: "/skola",
    desc: "Skolavslutning, friluftsdag och temadagar",
  },
  gymnasium: {
    label: "Gymnasium",
    path: "/gymnasium",
    desc: "Studentfirande, insparkar och elevkårsevent",
  },
  hogskola: {
    label: "Högskola & universitet",
    path: "/hogskola",
    desc: "Nollning, kårevent och studentkvällar",
  },
  kommun: {
    label: "Kommun",
    path: "/kommun",
    desc: "Sommarlovsaktiviteter, stadsfest och invigningar",
  },
};

const SEGMENT_ORDER = ["foretag", "massa", "skola", "gymnasium", "hogskola", "kommun"];

export const Route = createFileRoute("/sidkarta")({
  loader: async () => {
    const [catalogProducts, catalogCategories, sitemapData] = await Promise.all([
      listPublishedProducts(),
      listPublishedCategories(),
      getSitemapData(),
    ]);
    return { catalogProducts, catalogCategories, sitemapData };
  },
  component: SitemapPage,
  head: () => ({
    links: [{ rel: "canonical", href: "https://eventsverige.se/sidkarta" }],
    meta: [
      { title: "Sidkarta – Event Sverige" },
      {
        name: "description",
        content:
          "Översikt av alla sidor på Event Sverige. Hitta din inriktning och stad – från kickoff i Stockholm till skolavslutning i Halmstad.",
      },
    ],
  }),
});

function SitemapPage() {
  const { catalogProducts, catalogCategories, sitemapData } = Route.useLoaderData();
  const fetchData = useServerFn(getSitemapData);
  const { data, isLoading } = useQuery({
    queryKey: ["sitemap-data"],
    initialData: sitemapData,
    queryFn: () => fetchData(),
  });

  const grouped = (data?.pages ?? []).reduce<Record<string, { slug: string; name: string }[]>>(
    (acc, p) => {
      (acc[p.segment] ||= []).push({ slug: p.city_slug, name: p.city_name });
      return acc;
    },
    {},
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1">
        <section className="border-b bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">
              Sidkarta
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              Alla våra sidor på ett ställe. Välj inriktning och stad för lokal information,
              koncept, utrustning och vägar till en samlad offert.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-2xl font-bold mb-6">Huvudsidor</h2>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 text-sm">
            <Link to="/koncept" className="hover:text-accent">
              Våra koncept
            </Link>
            <Link to="/vinterevent" className="hover:text-accent">
              Vinterevent
            </Link>
            <Link to="/" className="hover:text-accent">
              Startsida
            </Link>
            <Link to="/produkter" className="hover:text-accent">
              Alla produkter
            </Link>
            <Link to="/hoppborgar" className="hover:text-accent">
              Hoppborgar
            </Link>
            <Link to="/tivolispel" className="hover:text-accent">
              Tivolispel & arkad
            </Link>
            <Link to="/scen-ljud-ljus" className="hover:text-accent">
              Scen, ljud & ljus
            </Link>
            <Link to="/om-oss" className="hover:text-accent">
              Om oss
            </Link>
            <Link to="/faq" className="hover:text-accent">
              Vanliga frågor
            </Link>
            <Link to="/kontakt" className="hover:text-accent">
              Kontakt
            </Link>
            <Link to="/partner" className="hover:text-accent">
              Bli partner
            </Link>
          </div>
        </section>

        <section className="experience-wrap py-12">
          <h2 className="font-display text-2xl font-bold mb-6">Koncept</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {visibleConcepts().map((c) => (
              <Link key={c.slug} to="/koncept/$slug" params={{ slug: c.slug }}>
                {c.name}
              </Link>
            ))}
          </div>
        </section>
        <section className="experience-wrap py-12">
          <h2 className="font-display text-2xl font-bold mb-6">Tidigare koncept</h2>
          <Link to="/koncept/$slug" params={{ slug: "parkens-aktivitetsdag" }}>
            Parkens Aktivitetsdag – arkiv
          </Link>
        </section>
        <section className="experience-wrap py-12">
          <h2 className="font-display text-2xl font-bold mb-6">Utrustningsbibliotek</h2>
          <p className="mb-6">
            Befintliga aktiviteter och kategorier. För ett sammanhållet upplägg börjar ni med våra
            koncept.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {catalogCategories.map((c) => (
              <Link key={c.slug} to="/kategori/$slug" params={{ slug: c.slug }}>
                {c.name}
              </Link>
            ))}
          </div>
          <h3 className="font-display text-xl font-bold mt-10 mb-6">Alla aktiviteter</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {catalogProducts.map((p) => (
              <Link key={p.slug} to="/produkter/$slug" params={{ slug: p.slug }}>
                {p.name}
              </Link>
            ))}
          </div>
        </section>
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
          <h2 className="font-display text-2xl font-bold mb-2">Inriktningar & städer</h2>
          <p className="text-muted-foreground mb-10">
            Klicka på en inriktning för översikt, eller direkt på en stad för lokal sida.
          </p>

          {isLoading ? (
            <p className="text-muted-foreground">Laddar…</p>
          ) : (
            <div className="space-y-12">
              {SEGMENT_ORDER.map((seg) => {
                const meta = SEGMENT_META[seg];
                const cities = grouped[seg] ?? [];
                if (!meta) return null;
                return (
                  <div key={seg} className="border-t pt-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
                      <h3 className="font-display text-xl font-bold">
                        <Link to={meta.path} className="hover:text-accent">
                          {meta.label}
                        </Link>
                      </h3>
                      <span className="text-xs text-muted-foreground">{cities.length} städer</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-5">{meta.desc}</p>
                    <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 text-sm">
                      {cities.map((c) => (
                        <li key={c.slug}>
                          <Link
                            to={`${meta.path}/${c.slug}` as string}
                            className="hover:text-accent"
                          >
                            {meta.label.split(" ")[0]} i {c.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
